import { z } from 'zod';
import { FetchStateSchema } from './connectors';

/**
 * Normalised connector value shapes (X3.3/X3.4/X3.5). A connector plugin maps a
 * provider payload into one of these, and a widget renders it — so **no provider
 * shape ever reaches a widget** (D0: swapping providers changes the registry and
 * the plugin, never widget code).
 *
 * Every value carries its own freshness so a widget can render the stale marker
 * from the `data_point` alone (docs/CONNECTORS.md §4: stale is marked, never
 * silently shown as fresh).
 */

const envelope = {
  fetch_state: FetchStateSchema,
  /** ISO instant of the last successful fetch this value came from. */
  fetched_at: z.string().datetime(),
  /** Attribution text the widget must render when the licence requires it. */
  attribution: z.object({ required: z.boolean(), text: z.string(), url: z.string().url().optional() }).optional()
};

// ---- weather (X3.3) ------------------------------------------------------
export const WeatherNowSchema = z.object({
  temp_c: z.number(),
  feels_like_c: z.number().optional(),
  condition: z.string().min(1),
  /** Optional provider-neutral glyph key (e.g. 'sun', 'rain'). */
  icon: z.string().optional(),
  wind_kph: z.number().optional(),
  humidity: z.number().min(0).max(100).optional(),
  uv: z.number().min(0).optional(),
  rain_chance: z.number().min(0).max(100).optional()
});
export const WeatherHourSchema = z.object({
  at: z.string().datetime(),
  temp_c: z.number(),
  condition: z.string().min(1),
  rain_chance: z.number().min(0).max(100).optional()
});
export const WeatherDaySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  min_c: z.number(),
  max_c: z.number(),
  condition: z.string().min(1),
  rain_chance: z.number().min(0).max(100).optional(),
  uv: z.number().min(0).optional()
});
export const WeatherValueSchema = z.object({
  ...envelope,
  units: z.enum(['metric', 'imperial']),
  location_label: z.string().max(120).optional(),
  now: WeatherNowSchema,
  hourly: z.array(WeatherHourSchema).default([]),
  /** At most a 7-day view (PLATFORM_EXPANSION.md §3). */
  daily: z.array(WeatherDaySchema).max(7).default([])
});
export type WeatherValue = z.infer<typeof WeatherValueSchema>;

// ---- news (X3.4) ---------------------------------------------------------
export const NewsItemSchema = z.object({
  title: z.string().min(1).max(400),
  source: z.string().min(1).max(120),
  /** Link attribution only — no full-text reproduction (§5.4). */
  url: z.string().url(),
  published_at: z.string().datetime().optional()
});
export const NewsValueSchema = z.object({
  ...envelope,
  items: z.array(NewsItemSchema).max(50).default([])
});
export type NewsValue = z.infer<typeof NewsValueSchema>;

// ---- calendar (X3.5) -----------------------------------------------------
export const CalendarEventSchema = z.object({
  title: z.string().min(1).max(400),
  start: z.string().datetime(),
  end: z.string().datetime().optional(),
  location: z.string().max(200).optional(),
  all_day: z.boolean().default(false)
});
export const CalendarValueSchema = z.object({
  ...envelope,
  events: z.array(CalendarEventSchema).max(50).default([])
});
export type CalendarValue = z.infer<typeof CalendarValueSchema>;

/**
 * Maps a connector data type to its value schema. A plugin's `normalize` output
 * is validated against this before it is written to `data_points`.
 */
export const CONNECTOR_VALUE_SCHEMAS = {
  weather: WeatherValueSchema,
  news: NewsValueSchema,
  calendar: CalendarValueSchema
} as const;

export type ConnectorValueDataType = keyof typeof CONNECTOR_VALUE_SCHEMAS;
